<template>
  <div class="system-settings">
    <div class="settings-container">
      <!-- 左侧菜单 -->
      <div class="settings-menu">
        <div class="menu-header">
          <h3>系统设置</h3>
          <el-button type="primary" size="small" @click="handleAddDict" :icon="Plus">
            新增配置
          </el-button>
        </div>
        <div class="menu-list">
          <div
            v-for="dict in dictList"
            :key="dict.id"
            class="menu-item"
            :class="{ active: selectedDict?.id === dict.id }"
            @click="selectDict(dict)"
          >
            <span class="menu-item-name">{{ getDictName(dict.code) }}</span>
            <span class="menu-item-code">{{ dict.code }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧内容 -->
      <div class="settings-content">
        <div v-if="!selectedDict" class="empty-content">
          <el-empty description="请选择左侧配置项" />
        </div>

        <div v-else class="content-editor">
          <div class="content-header">
            <h3>{{ getDictName(selectedDict.code) }}</h3>
            <div class="header-actions">
              <el-button @click="handleCancel">取消</el-button>
              <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
            </div>
          </div>

          <div class="content-body">
            <!-- 新年欢迎语配置 -->
            <div v-if="selectedDict.code === 'new_year'" class="config-section">
              <el-form :model="editData" label-width="120px">
                <el-form-item label="开始时间">
                  <el-date-picker
                    v-model="editData.startTime"
                    type="date"
                    placeholder="选择开始日期"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                  />
                </el-form-item>
                <el-form-item label="结束时间">
                  <el-date-picker
                    v-model="editData.endTime"
                    type="date"
                    placeholder="选择结束日期"
                    format="YYYY-MM-DD"
                    value-format="YYYY-MM-DD"
                  />
                </el-form-item>
                <el-form-item label="主题列表">
                  <div class="themes-list">
                    <el-button @click="addTheme" style="width: 100%; margin-bottom: 12px;">添加主题</el-button>
                    <div v-for="(theme, index) in reversedThemes" :key="index" class="theme-item">
                      <div class="theme-header">
                        <span>主题 {{ editData.themes.length - index }}</span>
                        <el-button type="danger" size="small" link @click="removeTheme(editData.themes.length - 1 - index)">删除</el-button>
                      </div>
                      <el-input v-model="theme.loadingMainTitle" placeholder="主标题" style="margin-bottom: 8px;" />
                      <el-input v-model="theme.loadingSubTitle" placeholder="副标题" style="margin-bottom: 8px;" />
                      <el-input v-model="theme.loadingTips" placeholder="加载提示" style="margin-bottom: 8px;" />
                      <div style="display: flex; gap: 8px;">
                        <el-input v-model="theme.leftLantern" placeholder="左灯笼" />
                        <el-input v-model="theme.rightLantern" placeholder="右灯笼" />
                      </div>
                    </div>
                  </div>
                </el-form-item>
              </el-form>
            </div>

            <!-- Burton配置 -->
            <div v-else-if="selectedDict.code === 'burton_config'" class="config-section">
              <el-form :model="editData" label-width="120px">
                <el-form-item label="账号">
                  <el-input v-model="editData.account" placeholder="请输入账号" />
                </el-form-item>
                <el-form-item label="密码">
                  <el-input v-model="editData.pwd" type="password" placeholder="请输入密码" show-password />
                </el-form-item>
                <el-form-item label="URL">
                  <el-input v-model="editData.url" placeholder="请输入URL" />
                </el-form-item>
                <el-form-item label="项目开始时间">
                  <el-date-picker
                    v-model="editData.project_start_time"
                    type="datetime"
                    placeholder="选择日期时间"
                    format="YYYY-MM-DD HH:mm:ss"
                    value-format="YYYY-MM-DD HH:mm:ss"
                  />
                </el-form-item>
              </el-form>
            </div>

            <!-- 日志服务项目管理 -->
            <div v-else-if="selectedDict.code === 'sls_log_product_list'" class="config-section">
              <div class="products-list">
                <el-button @click="addProduct" style="width: 100%; margin-bottom: 12px;">添加项目</el-button>
                <div v-for="(product, index) in reversedProducts" :key="index" class="product-item">
                  <div class="product-header">
                    <span>项目 {{ editData.length - index }}</span>
                    <div class="product-actions">
                      <el-button 
                        type="success" 
                        size="small" 
                        link 
                        @click="copySlsUrl(product)"
                        :icon="CopyDocument"
                      >
                        复制链接
                      </el-button>
                      <el-button 
                        type="primary" 
                        size="small" 
                        link 
                        @click="openSlsConsole(product)"
                        :icon="Link"
                      >
                        访问日志服务
                      </el-button>
                      <el-button type="danger" size="small" link @click="removeProduct(editData.length - 1 - index)">删除</el-button>
                    </div>
                  </div>
                  <el-form :model="product" label-width="100px">
                    <el-form-item label="项目名称">
                      <el-input v-model="product.name" placeholder="请输入项目名称" />
                    </el-form-item>
                    <el-form-item label="平台">
                      <el-select v-model="product.platform" placeholder="请选择平台">
                        <el-option label="莫凡阿里云" value="aliyun" />
                        <el-option label="莫凡聚石塔" value="mf" />
                      </el-select>
                    </el-form-item>
                    <el-form-item label="区域">
                      <el-input v-model="product.region" placeholder="请输入区域" />
                    </el-form-item>
                    <el-form-item label="项目">
                      <el-input v-model="product.project" placeholder="请输入项目" />
                    </el-form-item>
                    <el-form-item label="日志库">
                      <el-input v-model="product.logstore" placeholder="请输入日志库" />
                    </el-form-item>
                    <el-form-item label="消息字段">
                      <el-input v-model="product.fields.message" placeholder="消息字段名" />
                    </el-form-item>
                    <el-form-item label="时间字段">
                      <el-input v-model="product.fields['time:']" placeholder="时间字段名" />
                    </el-form-item>
                  </el-form>
                </div>
              </div>
            </div>

            <!-- 设置权限用户管理 -->
            <div v-else-if="selectedDict.code === 'setting_user_list'" class="config-section">
              <div class="user-list-container">
                <div class="user-list-header">
                  <span>授权用户列表</span>
                  <el-button type="primary" size="small" @click="showAddUserDialog = true">添加用户</el-button>
                </div>
                <div class="user-list">
                  <div 
                    v-for="(user, index) in editData" 
                    :key="index" 
                    class="user-item"
                    :class="{ 'current-user': isCurrentUser(user) }"
                  >
                    <span class="user-name">
                      {{ user }}
                      <el-tag v-if="isCurrentUser(user)" type="success" size="small" style="margin-left: 8px;">当前用户</el-tag>
                    </span>
                    <el-button type="danger" size="small" link @click="removeUser(index)">删除</el-button>
                  </div>
                  <div v-if="editData.length === 0" class="empty-user-list">
                    暂无授权用户
                  </div>
                </div>
              </div>
            </div>

            <!-- 导出报表管理员 -->
            <div v-else-if="selectedDict.code === 'export_admin_user_list'" class="config-section">
              <div class="user-list-container">
                <div class="user-list-header">
                  <span>导出报表管理员列表</span>
                  <el-button type="primary" size="small" @click="showAddExportAdminDialog = true">添加管理员</el-button>
                </div>
                <p style="font-size: 12px; color: #909399; margin: 0 0 12px;">
                  管理员可查看所有人的导出记录；普通用户只能看自己的导出记录。<br/>
                  填写登录用户名（username），例如 liang、chi。
                </p>
                <div class="user-list">
                  <div
                    v-for="(adminUser, index) in editData"
                    :key="index"
                    class="user-item"
                    :class="{ 'current-user': isCurrentUser(adminUser) }"
                  >
                    <span class="user-name">
                      {{ adminUser }}
                      <el-tag v-if="isCurrentUser(adminUser)" type="success" size="small" style="margin-left: 8px;">当前用户</el-tag>
                      <el-tag type="warning" size="small" style="margin-left: 8px;">管理员</el-tag>
                    </span>
                    <el-button type="danger" size="small" link @click="editData.splice(index, 1)">删除</el-button>
                  </div>
                  <div v-if="!editData.length" class="empty-user-list">
                    暂无管理员，所有人只能查看自己的导出记录
                  </div>
                </div>
              </div>
            </div>

            <!-- 未知配置项 - 显示JSON编辑器 -->
            <div v-else class="config-section">
              <el-input
                v-model="rawValue"
                type="textarea"
                :rows="20"
                placeholder="请输入JSON格式的配置"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增配置对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      title="新增配置"
      width="500px"
    >
      <el-form :model="newDict" label-width="80px">
        <el-form-item label="Code">
          <el-input v-model="newDict.code" placeholder="请输入配置代码" />
        </el-form-item>
        <el-form-item label="配置值">
          <el-input
            v-model="newDict.value"
            type="textarea"
            :rows="10"
            placeholder="请输入JSON格式的配置值"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAddDict" :loading="saving">确定</el-button>
      </template>
    </el-dialog>

    <!-- 添加用户对话框 -->
    <el-dialog v-model="showAddUserDialog" title="添加授权用户" width="400px">
      <el-form label-width="80px">
        <el-form-item label="用户名">
          <el-input v-model="newUsername" placeholder="请输入用户登录名" @keyup.enter="confirmAddUser" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showAddUserDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmAddUser">确定</el-button>
      </template>
    </el-dialog>

    <!-- 添加导出管理员对话框 -->
    <el-dialog v-model="showAddExportAdminDialog" title="添加导出报表管理员" width="400px">
      <el-form label-width="80px">
        <el-form-item label="用户名">
          <el-input v-model="newExportAdminUsername" placeholder="请输入登录用户名，例如 liang" @keyup.enter="confirmAddExportAdmin" />
        </el-form-item>
        <p style="font-size:12px;color:#909399;margin:0 0 0 80px;">填写 username（登录名），与字典 export_admin_user_list 中的值对应</p>
      </el-form>
      <template #footer>
        <el-button @click="showAddExportAdminDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmAddExportAdmin">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Link, CopyDocument } from '@element-plus/icons-vue'
import http from '@/utils/http'

const dictList = ref([])
const selectedDict = ref(null)
const editData = ref(null)
const rawValue = ref('')
const saving = ref(false)
const addDialogVisible = ref(false)
const newDict = ref({
  code: '',
  value: '{}'
})
const showAddUserDialog = ref(false)
const newUsername = ref('')
const showAddExportAdminDialog = ref(false)
const newExportAdminUsername = ref('')
const currentUser = ref(null)

// 获取当前登录用户
const user = JSON.parse(localStorage.getItem('auth-user') || 'null')

// 配置名称映射
const dictNameMap = {
  'new_year': '新年欢迎语',
  'burton_config': 'Burton商城看板',
  'sls_log_product_list': '日志服务项目管理',
  'setting_user_list': '设置权限用户管理',
  'export_admin_user_list': '导出报表管理员'
}

// 计算属性：倒序展示主题列表
const reversedThemes = computed(() => {
  if (!editData.value?.themes) return []
  return [...editData.value.themes].reverse()
})

// 计算属性：倒序展示项目列表
const reversedProducts = computed(() => {
  if (!Array.isArray(editData.value)) return []
  return [...editData.value].reverse()
})

const getDictName = (code) => {
  return dictNameMap[code] || code
}

const loadDicts = async () => {
  try {
    const data = await http.getAllDicts()
    dictList.value = data
  } catch (error) {
    console.error('加载配置列表失败:', error)
    ElMessage.error('加载配置列表失败')
  }
}

const selectDict = (dict) => {
  selectedDict.value = dict
  
  try {
    const value = JSON.parse(dict.value)
    
    // 根据不同的code处理数据
    if (dict.code === 'new_year') {
      editData.value = value
    } else if (dict.code === 'burton_config') {
      editData.value = value
    } else if (dict.code === 'sls_log_product_list') {
      editData.value = value
    } else if (dict.code === 'setting_user_list') {
      editData.value = value
    } else if (dict.code === 'export_admin_user_list') {
      editData.value = Array.isArray(value) ? value : []
    } else {
      // 未知配置，使用原始JSON字符串
      editData.value = null
      rawValue.value = dict.value
    }
  } catch (error) {
    console.error('解析配置失败:', error)
    editData.value = null
    rawValue.value = dict.value
  }
}

const handleSave = async () => {
  if (!selectedDict.value) return
  
  try {
    saving.value = true
    
    let valueToSave
    if (editData.value !== null) {
      valueToSave = JSON.stringify(editData.value)
    } else {
      // 验证JSON格式
      JSON.parse(rawValue.value)
      valueToSave = rawValue.value
    }
    
    await http.updateDict(selectedDict.value.id, {
      code: selectedDict.value.code,
      value: valueToSave
    })
    
    ElMessage.success('保存成功')
    await loadDicts()
    
    // 重新选中当前项
    const updated = dictList.value.find(d => d.id === selectedDict.value.id)
    if (updated) {
      selectDict(updated)
    }
  } catch (error) {
    console.error('保存失败:', error)
    if (error.response?.data?.code === 403) {
      ElMessage.error('无权限操作字典')
    } else {
      ElMessage.error('保存失败，请检查JSON格式是否正确')
    }
  } finally {
    saving.value = false
  }
}

const handleCancel = () => {
  if (selectedDict.value) {
    selectDict(selectedDict.value)
  }
}

const handleAddDict = () => {
  newDict.value = {
    code: '',
    value: '{}'
  }
  addDialogVisible.value = true
}

const confirmAddDict = async () => {
  if (!newDict.value.code) {
    ElMessage.warning('请输入配置代码')
    return
  }
  
  try {
    // 验证JSON格式
    JSON.parse(newDict.value.value)
    
    saving.value = true
    await http.addDict(newDict.value)
    
    ElMessage.success('新增成功')
    addDialogVisible.value = false
    await loadDicts()
  } catch (error) {
    console.error('新增失败:', error)
    if (error.response?.data?.code === 403) {
      ElMessage.error('无权限操作字典')
    } else {
      ElMessage.error('新增失败，请检查JSON格式是否正确')
    }
  } finally {
    saving.value = false
  }
}

// 新年主题操作
const addTheme = () => {
  if (!editData.value.themes) {
    editData.value.themes = []
  }
  // 添加到数组末尾
  editData.value.themes.push({
    loadingMainTitle: '',
    loadingSubTitle: '',
    loadingTips: '',
    leftLantern: '',
    rightLantern: ''
  })
}

const removeTheme = (index) => {
  editData.value.themes.splice(index, 1)
}

// 日志项目操作
const addProduct = () => {
  // 添加到数组末尾
  editData.value.push({
    name: '',
    platform: 'aliyun',
    region: '',
    project: '',
    logstore: '',
    fields: {
      message: 'message',
      'time:': '__time__'
    }
  })
}

const removeProduct = (index) => {
  editData.value.splice(index, 1)
}

// 打开阿里云SLS控制台
const openSlsConsole = (product) => {
  const url = generateSlsUrl(product)
  if (url) {
    window.open(url, '_blank')
  }
}

// 复制SLS链接
const copySlsUrl = async (product) => {
  const url = generateSlsUrl(product)
  if (!url) return
  
  try {
    await navigator.clipboard.writeText(url)
    ElMessage.success('链接已复制到剪贴板')
  } catch (error) {
    // 降级方案：使用传统方法复制
    const textarea = document.createElement('textarea')
    textarea.value = url
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    try {
      document.execCommand('copy')
      ElMessage.success('链接已复制到剪贴板')
    } catch (err) {
      ElMessage.error('复制失败，请手动复制')
      console.error('复制失败:', err)
    }
    document.body.removeChild(textarea)
  }
}

// 生成SLS访问URL
const generateSlsUrl = (product) => {
  if (!product.project || !product.logstore || !product.region) {
    ElMessage.warning('项目配置不完整，无法生成访问链接')
    return null
  }
  
  // 阿里云SLS控制台URL格式（莫凡聚石塔也是阿里云）
  return `https://sls.console.aliyun.com/lognext/project/${product.project}/logsearch/${product.logstore}?slsRegion=${product.region}`
}

// 用户管理操作
const isCurrentUser = (username) => {
  const currentUsername = user?.username || user?.loginName || user?.nick || user?.userName || ''
  return username === currentUsername
}

const removeUser = (index) => {
  editData.value.splice(index, 1)
}

const confirmAddUser = () => {
  if (!newUsername.value.trim()) {
    ElMessage.warning('请输入用户名')
    return
  }
  if (editData.value.includes(newUsername.value.trim())) {
    ElMessage.warning('该用户已存在')
    return
  }
  editData.value.push(newUsername.value.trim())
  newUsername.value = ''
  showAddUserDialog.value = false
  ElMessage.success('添加成功')
}

const confirmAddExportAdmin = () => {
  const name = newExportAdminUsername.value.trim()
  if (!name) {
    ElMessage.warning('请输入用户名')
    return
  }
  if (!Array.isArray(editData.value)) editData.value = []
  if (editData.value.includes(name)) {
    ElMessage.warning('该管理员已存在')
    return
  }
  editData.value.push(name)
  newExportAdminUsername.value = ''
  showAddExportAdminDialog.value = false
  ElMessage.success('添加成功，记得点保存')
}

onMounted(() => {
  loadDicts()
})
</script>

<style scoped>
.system-settings {
  height: calc(100vh - 120px);
  padding: 16px;
  background: #f5f7fa;
}

.settings-container {
  display: flex;
  height: 100%;
  gap: 16px;
}

.settings-menu {
  width: 280px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.menu-header {
  padding: 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.menu-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.menu-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.menu-item {
  padding: 12px 16px;
  margin-bottom: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu-item:hover {
  background: #f5f7fa;
}

.menu-item.active {
  background: #ecf5ff;
  color: #409eff;
}

.menu-item-name {
  font-size: 14px;
  font-weight: 500;
}

.menu-item-code {
  font-size: 12px;
  color: #909399;
}

.settings-content {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.empty-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.content-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-header {
  padding: 16px 24px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.content-header h3 {
  margin: 0;
  font-size: 18px;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.content-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.config-section {
  max-width: 800px;
}

.themes-list,
.products-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.theme-item,
.product-item {
  padding: 16px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fafafa;
}

.theme-header,
.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 500;
  color: #606266;
}

.product-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.user-list-container {
  max-width: 600px;
}

.user-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.user-list {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  transition: background-color 0.2s;
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background-color: #f5f7fa;
}

.user-item.current-user {
  background-color: #ecf5ff;
  border-left: 3px solid #409eff;
}

.user-name {
  font-size: 14px;
  color: #606266;
  display: flex;
  align-items: center;
}

.empty-user-list {
  padding: 40px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
</style>
